import { useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { Link } from "wouter";

export default function TermsOfService() {
  const lastUpdated = "January 21, 2026";
  
  useEffect(() => {
    document.title = "Terms of Service | Eatzy Kudoz";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", "Eatzy Kudoz Terms of Service - Read our terms and conditions for using our restaurant discovery, reviews, and rewards platform.");
    } else {
      const meta = document.createElement('meta');
      meta.name = "description";
      meta.content = "Eatzy Kudoz Terms of Service - Read our terms and conditions for using our restaurant discovery, reviews, and rewards platform.";
      document.head.appendChild(meta);
    }
    
    return () => {
      document.title = "Eatzy Kudoz";
    };
  }, []);
  
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-6">
          <Link href="/login">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          </Link>
        </div>

        <Card>
          <CardContent className="p-6 md:p-8">
            <h1 className="text-3xl font-bold mb-2">Terms of Service</h1>
            <p className="text-muted-foreground mb-8">Last Updated: {lastUpdated}</p>

            <div className="prose prose-sm max-w-none dark:prose-invert space-y-6">
              <section>
                <h2 className="text-xl font-semibold mb-3">1. Agreement to Terms</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Welcome to Eatzy Kudoz! These Terms of Service ("Terms") constitute a legally binding agreement between you and Eatzy LLC ("Eatzy," "we," "our," or "us") governing your access to and use of the Eatzy Kudoz mobile application, website, and related services (collectively, the "Platform").
                </p>
                <p className="text-muted-foreground leading-relaxed mt-3">
                  By creating an account, accessing, or using our Platform, you agree to be bound by these Terms and our Privacy Policy. If you do not agree to these Terms, you may not access or use the Platform.
                </p>
                <p className="text-muted-foreground leading-relaxed mt-3">
                  We reserve the right to modify these Terms at any time. We will notify you of material changes by posting the updated Terms on our Platform and updating the "Last Updated" date. Your continued use of the Platform after such changes constitutes your acceptance of the modified Terms.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">2. Eligibility</h2>
                <p className="text-muted-foreground leading-relaxed">
                  You must be at least 13 years of age to use the Platform. By using the Platform, you represent and warrant that:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-3">
                  <li>You are at least 13 years old</li>
                  <li>If you are under 18 years old, you have obtained parental or guardian consent to use the Platform</li>
                  <li>You have the legal capacity to enter into these Terms</li>
                  <li>You are not prohibited from using the Platform under applicable laws</li>
                  <li>Your use of the Platform will not violate any applicable law or regulation</li>
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-3">
                  If you are using the Platform on behalf of a business or other entity, you represent that you have the authority to bind that entity to these Terms.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">3. Description of Services</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Eatzy Kudoz is a restaurant discovery and engagement platform that provides the following services:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-3">
                  <li><strong>Restaurant Discovery:</strong> Browse and search for restaurants based on location, cuisine, ratings, and user reviews</li>
                  <li><strong>Reviews and Ratings:</strong> Share your dining experiences through written reviews, photos, videos, and star ratings</li>
                  <li><strong>Kudoz Rewards Program:</strong> Earn "Kudoz" points for engagement activities such as writing reviews, referring friends, and interacting with the community</li>
                  <li><strong>Social Features:</strong> Follow other users, like and comment on reviews, and connect with fellow food enthusiasts</li>
                  <li><strong>Food Ordering:</strong> Place orders directly with participating restaurants through our integrated ordering system</li>
                  <li><strong>Badges and Achievements:</strong> Earn recognition badges for your contributions to the community</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">4. User Accounts</h2>
                
                <h3 className="text-lg font-medium mt-4 mb-2">4.1 Account Registration</h3>
                <p className="text-muted-foreground leading-relaxed">
                  To access certain features of the Platform, you must create an account. You agree to provide accurate, current, and complete information during registration and to update such information to keep it accurate, current, and complete.
                </p>

                <h3 className="text-lg font-medium mt-4 mb-2">4.2 Account Security</h3>
                <p className="text-muted-foreground leading-relaxed">
                  You are responsible for safeguarding your account credentials and for all activities that occur under your account. You agree to:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-2">
                  <li>Create a strong, unique password for your account</li>
                  <li>Not share your login credentials with any third party</li>
                  <li>Immediately notify us of any unauthorized use of your account</li>
                  <li>Log out of your account at the end of each session on shared devices</li>
                </ul>

                <h3 className="text-lg font-medium mt-4 mb-2">4.3 Account Termination</h3>
                <p className="text-muted-foreground leading-relaxed">
                  You may delete your account at any time through your account settings. We reserve the right to suspend or terminate your account if you violate these Terms or engage in conduct that we determine, in our sole discretion, is harmful to other users, third parties, or the Platform.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">5. User Content</h2>
                
                <h3 className="text-lg font-medium mt-4 mb-2">5.1 Your Content</h3>
                <p className="text-muted-foreground leading-relaxed">
                  "User Content" means any content you submit, post, or transmit through the Platform, including reviews, ratings, photos, videos, comments, and profile information. You retain ownership of your User Content, but by posting it on the Platform, you grant us a non-exclusive, worldwide, royalty-free, sublicensable, and transferable license to use, reproduce, modify, display, and distribute your User Content in connection with operating and promoting the Platform.
                </p>

                <h3 className="text-lg font-medium mt-4 mb-2">5.2 Content Guidelines</h3>
                <p className="text-muted-foreground leading-relaxed">
                  You agree that your User Content will not:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-2">
                  <li>Be false, misleading, or inaccurate</li>
                  <li>Infringe any third party's intellectual property, privacy, or other rights</li>
                  <li>Contain defamatory, harassing, threatening, or abusive language</li>
                  <li>Include hate speech or content that promotes discrimination</li>
                  <li>Contain sexually explicit or pornographic material</li>
                  <li>Promote violence or illegal activities</li>
                  <li>Include spam, advertising, or solicitations without our prior consent</li>
                  <li>Contain malicious code, viruses, or harmful software</li>
                  <li>Impersonate any person or entity</li>
                  <li>Violate any applicable law or regulation</li>
                </ul>

                <h3 className="text-lg font-medium mt-4 mb-2">5.3 Review Guidelines</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Reviews should reflect your genuine dining experiences. You agree to:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-2">
                  <li>Only review restaurants you have actually visited</li>
                  <li>Provide honest and fair assessments of your experience</li>
                  <li>Not accept compensation in exchange for writing positive or negative reviews</li>
                  <li>Disclose any material connections to restaurants you review (e.g., if you are an employee or owner)</li>
                  <li>Not review your own business or a competitor's business</li>
                </ul>

                <h3 className="text-lg font-medium mt-4 mb-2">5.4 Content Moderation</h3>
                <p className="text-muted-foreground leading-relaxed">
                  We reserve the right, but have no obligation, to monitor, edit, or remove User Content that we determine, in our sole discretion, violates these Terms or is otherwise objectionable. We may also take action against accounts that repeatedly violate our content guidelines.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">6. Kudoz Rewards Program</h2>
                
                <h3 className="text-lg font-medium mt-4 mb-2">6.1 Earning Kudoz</h3>
                <p className="text-muted-foreground leading-relaxed">
                  You may earn Kudoz points by engaging with the Platform in various ways, including writing reviews, uploading photos, referring friends, and participating in promotions. The specific activities that earn Kudoz and the point values are determined by us and may change at any time without notice.
                </p>

                <h3 className="text-lg font-medium mt-4 mb-2">6.2 Redeeming Kudoz</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Kudoz points may be redeemed for rewards as made available through the Platform. Redemption values and available rewards are subject to change. Kudoz points have no cash value and cannot be exchanged for cash, transferred to other users, or sold.
                </p>

                <h3 className="text-lg font-medium mt-4 mb-2">6.3 Program Rules</h3>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li>Kudoz points are personal to your account and non-transferable</li>
                  <li>We reserve the right to adjust, void, or expire Kudoz points in cases of fraud, abuse, or violation of these Terms</li>
                  <li>Kudoz points may expire if your account is inactive for an extended period</li>
                  <li>We may modify, suspend, or terminate the Kudoz program at any time</li>
                  <li>Upon account termination, any accumulated Kudoz points will be forfeited</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">7. Food Orders and Payments</h2>
                
                <h3 className="text-lg font-medium mt-4 mb-2">7.1 Restaurant Transactions</h3>
                <p className="text-muted-foreground leading-relaxed">
                  When you place a food order through the Platform, you are entering into a transaction directly with the restaurant. Eatzy Kudoz acts as a technology platform facilitating the connection between you and restaurants but is not a party to your transaction with any restaurant.
                </p>

                <h3 className="text-lg font-medium mt-4 mb-2">7.2 Pricing and Payment</h3>
                <p className="text-muted-foreground leading-relaxed">
                  All prices displayed on the Platform are set by the individual restaurants and are subject to change. You agree to pay all charges associated with your orders, including food costs, taxes, delivery fees, and tips where applicable. Payments are processed directly between you and the restaurant.
                </p>

                <h3 className="text-lg font-medium mt-4 mb-2">7.3 Refunds and Cancellations</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Refunds for food orders are at the sole discretion of the individual restaurant from which you ordered. If you have an issue with an order, please contact the restaurant directly. We may assist in facilitating communication but are not responsible for issuing refunds or resolving disputes between you and restaurants.
                </p>

                <h3 className="text-lg font-medium mt-4 mb-2">7.4 Order Accuracy</h3>
                <p className="text-muted-foreground leading-relaxed">
                  You are responsible for ensuring the accuracy of your order details, including delivery address, contact information, and order specifications. Restaurants are responsible for the quality and preparation of food items.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">8. Intellectual Property</h2>
                
                <h3 className="text-lg font-medium mt-4 mb-2">8.1 Our Intellectual Property</h3>
                <p className="text-muted-foreground leading-relaxed">
                  The Platform and its entire contents, features, and functionality (including but not limited to all information, software, text, displays, images, video, audio, and the design, selection, and arrangement thereof) are owned by Eatzy LLC, its licensors, or other providers of such material and are protected by United States and international copyright, trademark, patent, trade secret, and other intellectual property or proprietary rights laws.
                </p>

                <h3 className="text-lg font-medium mt-4 mb-2">8.2 Trademarks</h3>
                <p className="text-muted-foreground leading-relaxed">
                  "Eatzy," "Eatzy Kudoz," "Kudoz," and our logos are trademarks of Eatzy LLC. You may not use these marks without our prior written permission. Other trademarks, service marks, and logos used on the Platform are the property of their respective owners.
                </p>

                <h3 className="text-lg font-medium mt-4 mb-2">8.3 Limited License</h3>
                <p className="text-muted-foreground leading-relaxed">
                  We grant you a limited, non-exclusive, non-transferable, revocable license to access and use the Platform for your personal, non-commercial use. This license does not include the right to modify, copy, distribute, transmit, display, perform, reproduce, publish, license, create derivative works from, transfer, or sell any information, content, software, products, or services obtained from the Platform.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">9. Prohibited Conduct</h2>
                <p className="text-muted-foreground leading-relaxed">
                  You agree not to:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-3">
                  <li>Use the Platform for any unlawful purpose or in violation of these Terms</li>
                  <li>Create fake accounts, reviews, or engagement to manipulate the Platform</li>
                  <li>Use bots, scrapers, or automated systems to access the Platform</li>
                  <li>Attempt to gain unauthorized access to the Platform or other users' accounts</li>
                  <li>Interfere with or disrupt the Platform's servers or networks</li>
                  <li>Reverse engineer, decompile, or disassemble any part of the Platform</li>
                  <li>Use the Platform to send unsolicited communications or spam</li>
                  <li>Harass, stalk, or threaten other users</li>
                  <li>Collect or store personal data about other users without their consent</li>
                  <li>Use the Platform in a way that could damage, disable, or impair it</li>
                  <li>Circumvent any content-filtering techniques we employ</li>
                  <li>Use the Platform to compete with Eatzy Kudoz without our written consent</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">10. Third-Party Services</h2>
                <p className="text-muted-foreground leading-relaxed">
                  The Platform may contain links to third-party websites, services, or content that are not owned or controlled by us. We have no control over, and assume no responsibility for, the content, privacy policies, or practices of any third-party websites or services. By using the Platform, you expressly release us from any and all liability arising from your use of any third-party website or service.
                </p>
                <p className="text-muted-foreground leading-relaxed mt-3">
                  The Platform integrates with third-party services including social media platforms (Facebook, Instagram, Google, Apple) for authentication and content sharing. Your use of these integrations is subject to the respective third-party terms of service and privacy policies.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">11. Disclaimer of Warranties</h2>
                <p className="text-muted-foreground leading-relaxed">
                  THE PLATFORM IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT ANY WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR COURSE OF PERFORMANCE.
                </p>
                <p className="text-muted-foreground leading-relaxed mt-3">
                  WE DO NOT WARRANT THAT: (A) THE PLATFORM WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE; (B) ANY ERRORS WILL BE CORRECTED; (C) THE PLATFORM IS FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS; (D) THE RESULTS OF USING THE PLATFORM WILL MEET YOUR REQUIREMENTS; OR (E) USER CONTENT OR RESTAURANT INFORMATION IS ACCURATE, COMPLETE, OR RELIABLE.
                </p>
                <p className="text-muted-foreground leading-relaxed mt-3">
                  We do not endorse, warrant, or guarantee any restaurants, menu items, or services offered through the Platform. You assume all risk related to your interactions with restaurants and consumption of food ordered through the Platform.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">12. Limitation of Liability</h2>
                <p className="text-muted-foreground leading-relaxed">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, EATZY LLC, ITS AFFILIATES, OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, AND LICENSORS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, ARISING OUT OF OR RELATED TO YOUR USE OF OR INABILITY TO USE THE PLATFORM.
                </p>
                <p className="text-muted-foreground leading-relaxed mt-3">
                  IN NO EVENT SHALL OUR TOTAL LIABILITY TO YOU EXCEED THE GREATER OF: (A) THE AMOUNTS YOU HAVE PAID TO US IN THE TWELVE (12) MONTHS PRIOR TO THE CLAIM; OR (B) ONE HUNDRED DOLLARS ($100).
                </p>
                <p className="text-muted-foreground leading-relaxed mt-3">
                  These limitations apply regardless of the legal theory on which the claim is based and even if we have been advised of the possibility of such damages.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">13. Indemnification</h2>
                <p className="text-muted-foreground leading-relaxed">
                  You agree to defend, indemnify, and hold harmless Eatzy LLC, its affiliates, licensors, and service providers, and its and their respective officers, directors, employees, contractors, agents, licensors, suppliers, successors, and assigns from and against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising out of or relating to: (a) your violation of these Terms; (b) your User Content; (c) your use of the Platform; or (d) your violation of any third-party rights.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">14. Dispute Resolution</h2>
                
                <h3 className="text-lg font-medium mt-4 mb-2">14.1 Governing Law</h3>
                <p className="text-muted-foreground leading-relaxed">
                  These Terms and any dispute arising out of or related to them shall be governed by and construed in accordance with the laws of the State of Connecticut, United States, without regard to its conflict of law principles.
                </p>

                <h3 className="text-lg font-medium mt-4 mb-2">14.2 Informal Resolution</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Before filing a claim against us, you agree to try to resolve the dispute informally by contacting us at legal@eatzy.com. We will try to resolve the dispute informally by contacting you via email. If a dispute is not resolved within 30 days of submission, you or we may bring a formal proceeding.
                </p>

                <h3 className="text-lg font-medium mt-4 mb-2">14.3 Venue</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Any legal suit, action, or proceeding arising out of or related to these Terms or the Platform shall be instituted exclusively in the federal courts of the United States or the courts of the State of Connecticut, in each case located in Hartford County. You waive any objection to the exercise of jurisdiction over you by such courts and to venue in such courts.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">15. General Provisions</h2>
                
                <h3 className="text-lg font-medium mt-4 mb-2">15.1 Entire Agreement</h3>
                <p className="text-muted-foreground leading-relaxed">
                  These Terms, together with our Privacy Policy, constitute the entire agreement between you and Eatzy LLC regarding your use of the Platform and supersede all prior and contemporaneous understandings, agreements, representations, and warranties.
                </p>

                <h3 className="text-lg font-medium mt-4 mb-2">15.2 Severability</h3>
                <p className="text-muted-foreground leading-relaxed">
                  If any provision of these Terms is held to be invalid, illegal, or unenforceable, the remaining provisions shall continue in full force and effect.
                </p>

                <h3 className="text-lg font-medium mt-4 mb-2">15.3 Waiver</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Our failure to enforce any right or provision of these Terms shall not be deemed a waiver of such right or provision.
                </p>

                <h3 className="text-lg font-medium mt-4 mb-2">15.4 Assignment</h3>
                <p className="text-muted-foreground leading-relaxed">
                  You may not assign or transfer these Terms or any of your rights or obligations hereunder without our prior written consent. We may assign these Terms without restriction.
                </p>

                <h3 className="text-lg font-medium mt-4 mb-2">15.5 Notices</h3>
                <p className="text-muted-foreground leading-relaxed">
                  We may provide notices to you via email, push notifications, or by posting on the Platform. You agree that all agreements, notices, disclosures, and other communications we provide electronically satisfy any legal requirement that such communications be in writing.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold mb-3">16. Contact Information</h2>
                <p className="text-muted-foreground leading-relaxed">
                  If you have any questions about these Terms of Service, please contact us at:
                </p>
                <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                  <p className="font-medium">Eatzy LLC</p>
                  <p className="text-muted-foreground mt-1">Email: legal@eatzy.com</p>
                  <p className="text-muted-foreground">Website: www.eatzy.com</p>
                </div>
              </section>
            </div>
          </CardContent>
        </Card>

        <div className="mt-6 text-center">
          <Link href="/privacy">
            <Button variant="ghost" className="text-muted-foreground underline">
              View Privacy Policy
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
